import { createReducer, on } from '@ngrx/store';
import * as baseState from '../../../../core/models/base-state/base-state';
import { QuestionnaireDetail } from '../../models/questionnaire-detail';
import { QuestionnaireHistoryEntry } from '../../models/questionnaire-history-entry';
import { ScreeningDetail } from '../../models/screening-detail';
import { ScreeningHistoryEntry } from '../../models/screening-history-entry';
import { ScreeningsSummary } from '../../models/screenings-summary';
import { StudyReminderDetail } from '../../models/study-reminder-detail';
import * as screeningsActions from '../actions/screenings.actions';

export const screeningsFeatureKey = 'screenings';

/**
 * One slice, one sub-state per view. Each view owns its own data + BaseState (loading/error) so a
 * fetch/skeleton/error on one screen never clobbers another. The slice resets to initialState on an
 * in-account patient switch automatically via resetPatientStateMetaReducer — no per-reducer wiring.
 */
export interface ScreeningsViewState<T> {
  data: T | null;
  baseState: baseState.BaseState;
}

export interface ScreeningsState {
  summary: ScreeningsViewState<ScreeningsSummary>;
  questionnaireHistory: ScreeningsViewState<QuestionnaireHistoryEntry[]>;
  questionnaireDetail: ScreeningsViewState<QuestionnaireDetail>;
  screeningHistory: ScreeningsViewState<ScreeningHistoryEntry[]>;
  screeningDetail: ScreeningsViewState<ScreeningDetail>;
  reminderDetail: ScreeningsViewState<StudyReminderDetail>;
}

const createViewState = <T>(): ScreeningsViewState<T> => ({
  data: null,
  baseState: baseState.createBaseState(undefined),
});

export const initialState: ScreeningsState = {
  summary: createViewState<ScreeningsSummary>(),
  questionnaireHistory: createViewState<QuestionnaireHistoryEntry[]>(),
  questionnaireDetail: createViewState<QuestionnaireDetail>(),
  screeningHistory: createViewState<ScreeningHistoryEntry[]>(),
  screeningDetail: createViewState<ScreeningDetail>(),
  reminderDetail: createViewState<StudyReminderDetail>(),
};

// Per-view transitions. Kept as tiny helpers so every view reduces identically: a load clears the
// prior error and flips isLoading; a success stores data and clears loading/error; a failure records
// the error and clears loading while leaving any previously loaded data in place.
const startLoad = <T>(view: ScreeningsViewState<T>): ScreeningsViewState<T> => ({
  ...view,
  baseState: { ...view.baseState, isLoading: true, error: null },
});

const finishLoad = <T>(view: ScreeningsViewState<T>, data: T): ScreeningsViewState<T> => ({
  data,
  baseState: { ...view.baseState, isLoading: false, error: null },
});

const failLoad = <T>(view: ScreeningsViewState<T>, error: unknown): ScreeningsViewState<T> => ({
  ...view,
  baseState: { ...view.baseState, isLoading: false, error },
});

export const screeningsReducer = createReducer(
  initialState,

  // Summary
  on(
    screeningsActions.getScreeningsSummary,
    (state): ScreeningsState => ({ ...state, summary: startLoad(state.summary) })
  ),
  on(
    screeningsActions.getScreeningsSummarySuccess,
    (state, { summary }): ScreeningsState => ({ ...state, summary: finishLoad(state.summary, summary) })
  ),
  on(
    screeningsActions.getScreeningsSummaryFailure,
    (state, { error }): ScreeningsState => ({ ...state, summary: failLoad(state.summary, error) })
  ),

  // Questionnaire history
  on(
    screeningsActions.getQuestionnaireHistory,
    (state): ScreeningsState => ({ ...state, questionnaireHistory: startLoad(state.questionnaireHistory) })
  ),
  on(
    screeningsActions.getQuestionnaireHistorySuccess,
    (state, { entries }): ScreeningsState => ({
      ...state,
      questionnaireHistory: finishLoad(state.questionnaireHistory, entries),
    })
  ),
  on(
    screeningsActions.getQuestionnaireHistoryFailure,
    (state, { error }): ScreeningsState => ({
      ...state,
      questionnaireHistory: failLoad(state.questionnaireHistory, error),
    })
  ),

  // Questionnaire detail
  on(
    screeningsActions.getQuestionnaireDetail,
    (state): ScreeningsState => ({ ...state, questionnaireDetail: startLoad(state.questionnaireDetail) })
  ),
  on(
    screeningsActions.getQuestionnaireDetailSuccess,
    (state, { detail }): ScreeningsState => ({
      ...state,
      questionnaireDetail: finishLoad(state.questionnaireDetail, detail),
    })
  ),
  on(
    screeningsActions.getQuestionnaireDetailFailure,
    (state, { error }): ScreeningsState => ({
      ...state,
      questionnaireDetail: failLoad(state.questionnaireDetail, error),
    })
  ),

  // Screening (procedure) history
  on(
    screeningsActions.getScreeningHistory,
    (state): ScreeningsState => ({ ...state, screeningHistory: startLoad(state.screeningHistory) })
  ),
  on(
    screeningsActions.getScreeningHistorySuccess,
    (state, { entries }): ScreeningsState => ({
      ...state,
      screeningHistory: finishLoad(state.screeningHistory, entries),
    })
  ),
  on(
    screeningsActions.getScreeningHistoryFailure,
    (state, { error }): ScreeningsState => ({ ...state, screeningHistory: failLoad(state.screeningHistory, error) })
  ),

  // Screening (procedure) detail
  on(
    screeningsActions.getScreeningDetail,
    (state): ScreeningsState => ({ ...state, screeningDetail: startLoad(state.screeningDetail) })
  ),
  on(
    screeningsActions.getScreeningDetailSuccess,
    (state, { detail }): ScreeningsState => ({ ...state, screeningDetail: finishLoad(state.screeningDetail, detail) })
  ),
  on(
    screeningsActions.getScreeningDetailFailure,
    (state, { error }): ScreeningsState => ({ ...state, screeningDetail: failLoad(state.screeningDetail, error) })
  ),

  // Study-reminder detail
  on(
    screeningsActions.getReminderDetail,
    (state): ScreeningsState => ({ ...state, reminderDetail: startLoad(state.reminderDetail) })
  ),
  on(
    screeningsActions.getReminderDetailSuccess,
    (state, { detail }): ScreeningsState => ({ ...state, reminderDetail: finishLoad(state.reminderDetail, detail) })
  ),
  on(
    screeningsActions.getReminderDetailFailure,
    (state, { error }): ScreeningsState => ({ ...state, reminderDetail: failLoad(state.reminderDetail, error) })
  )
);
